import { Link } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { schemaGraph } from '../../utils/schema';

export const GUIDE_INDEX = [
  {
    url: '/arizona-real-estate-ce-requirements',
    title: 'Arizona Real Estate CE Requirements: The Complete 2026 Guide',
    desc: 'Every ADRE CE requirement explained: the 24-hour breakdown, mandatory categories, 2025 rule changes, and deadlines.',
    label: 'Pillar Guide',
  },
  {
    url: '/arizona/how-to-renew',
    title: 'How to Renew Your Arizona Real Estate License (Step by Step)',
    desc: 'The full renewal in five steps: finish 24 hours, enter them in the ADRE Licensee Login, click Renew, pay the fee.',
    label: 'How to Renew',
  },
  {
    url: '/arizona-ce-2025-rule-changes',
    title: "Arizona's New CE Rules: Water, Firewise & Deed Fraud",
    desc: 'ADRE now requires 1 hour each of Arizona Water, Firewise, and Deed Fraud CE. What changed, who it affects, and how to satisfy it.',
    label: '2025 Rules',
  },
  {
    url: '/when-does-my-arizona-license-expire',
    title: 'When Does My Arizona Real Estate License Expire?',
    desc: 'How to find your exact license expiration date and the renewal timeline that avoids the $125 late fee.',
    label: 'Renewal Date',
  },
  {
    url: '/arizona-license-expired-late-renewal',
    title: 'Your Arizona License Expired. Now What?',
    desc: 'What the $125 late fee covers, the one-year deadline, what you can\'t do while expired, and how to fix it fast.',
    label: 'Late Renewal',
  },
  {
    url: '/how-to-submit-ce-to-adre',
    title: 'How to Submit CE Hours to ADRE (Step-by-Step)',
    desc: 'Your school doesn\'t report CE to ADRE — you do. How to enter certificates in the Licensee Login portal.',
    label: 'Portal Guide',
  },
  {
    url: '/how-fast-can-i-finish-arizona-ce',
    title: 'How Fast Can You Finish 24 Hours of Arizona CE?',
    desc: 'ADRE caps CE at 9 hours per day, so 24 hours takes a minimum of 3 days. The realistic fastest schedule.',
    label: 'Time Planning',
  },
  {
    url: '/arizona-ce-online',
    title: 'Can You Complete All Arizona Real Estate CE Online? Yes.',
    desc: 'All 24 Arizona CE hours can be done online — no classroom required. How online ADRE-approved CE works.',
    label: 'Format',
  },
  {
    url: '/arizona-ce-faq',
    title: 'Arizona Real Estate CE FAQ',
    desc: 'Quick answers: Code of Ethics, out-of-state CE, electives, category deficiencies, broker pre-licensing waivers, and more.',
    label: 'FAQ',
  },
  {
    url: '/arizona-ce-provider-comparison',
    title: 'Arizona Real Estate CE Providers Compared (2026)',
    desc: 'Every ADRE-approved provider side by side: course formats, focus, and how to check a package covers the 2025 topics.',
    label: 'Comparison',
  },
];

export default function ResourcesHubPage() {
  useSEO({
    title: 'Arizona Real Estate CE Resources | Steady Education',
    description: 'Plain-English guides on Arizona real estate CE: requirements, 2025 rule changes, fastest path to renewal, and more. Updated June 2026.',
    url: canonicalUrl('/resources'),
    jsonLd: schemaGraph({
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: 'Resources', url: 'https://steadyeducation.com/resources' },
      ],
    }),
  });

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div className="container">
          <h1 style={styles.heroTitle}>Arizona Real Estate CE Resources</h1>
          <p style={styles.heroSubtitle}>
            Plain-English guides on Arizona's CE requirements — written for licensees, updated for 2026 rules,
            verified against azre.gov.
          </p>
        </div>
      </div>

      <div className="container" style={styles.content}>
        <div style={styles.grid}>
          {GUIDE_INDEX.map(g => (
            <Link key={g.url} to={g.url} style={styles.card}>
              <div style={styles.cardLabel}>{g.label}</div>
              <div style={styles.cardTitle}>{g.title}</div>
              <div style={styles.cardDesc}>{g.desc}</div>
              <div style={styles.cardArrow}>Read guide →</div>
            </Link>
          ))}
        </div>

        <div style={styles.ctaSection}>
          <div style={styles.ctaInner}>
            <div>
              <div style={styles.ctaLabel}>Ready to renew?</div>
              <div style={styles.ctaTitle}>Complete Arizona CE Package — $49.99</div>
              <div style={styles.ctaSub}>All 24 ADRE-required hours including 2025 Water, Firewise & Deed Fraud. Self-paced, instant certificates.</div>
            </div>
            <Link to="/arizona/courses" style={styles.ctaBtn}>See the package →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { background: '#FAF7F2', minHeight: '100vh' },
  hero: { background: 'linear-gradient(135deg, #2C453E 0%, #243A34 100%)', color: '#EFE6D6', padding: '48px 0 40px' },
  heroTitle: { fontFamily: 'var(--font-display)', fontSize: 40, lineHeight: 1.15, color: 'white', marginBottom: 12 },
  heroSubtitle: { fontSize: 17, lineHeight: 1.6, maxWidth: 720, color: 'rgba(239,230,214,0.85)' },
  content: { padding: '40px 0 60px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 20, marginBottom: 40 },
  card: { background: 'white', border: '1px solid #e8e2d6', borderRadius: 14, padding: 24, textDecoration: 'none', color: '#243A34', display: 'flex', flexDirection: 'column', gap: 10, transition: 'all 0.15s' },
  cardLabel: { display: 'inline-block', alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, color: '#2C453E', background: '#EFE6D6', padding: '3px 10px', borderRadius: 999, letterSpacing: '0.02em', textTransform: 'uppercase' },
  cardTitle: { fontFamily: 'var(--font-display)', fontSize: 18, lineHeight: 1.3, color: '#243A34', marginTop: 4 },
  cardDesc: { fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.55, flex: 1 },
  cardArrow: { fontSize: 13, fontWeight: 700, color: '#2C453E', marginTop: 6 },
  ctaSection: { background: 'linear-gradient(135deg, #2C453E, #243A34)', borderRadius: 16, padding: 32, color: 'white' },
  ctaInner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' },
  ctaLabel: { fontSize: 12, color: '#B6A189', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 },
  ctaTitle: { fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 6 },
  ctaSub: { fontSize: 14, color: 'rgba(255,255,255,0.75)', maxWidth: 540 },
  ctaBtn: { display: 'inline-block', background: '#B6A189', color: 'white', textDecoration: 'none', fontWeight: 700, padding: '14px 28px', borderRadius: 10, fontSize: 15, whiteSpace: 'nowrap' },
};
